import { useCallback, useEffect, useState } from "react";
import {
  createSaleDepositEditRequest,
  getSaleDepositEditRequests,
} from "../../services";
import { ClosingBranchDay } from "../SaleDepositReport/saleCenterClosing.utils";

/**
 * How many outstanding requests are read for one branch-day.
 *
 * A request covers one deposit and a deposit is one สายส่ง of the day, so the ceiling is the
 * number of routes a branch runs — well inside a single page. Asking for one page and no
 * more keeps this to a single request per branch-day opened.
 */
const PENDING_REQUEST_PAGE_LIMIT = 100;

interface DepositEditRequests {
  /** Deposits in this branch-day that already have a request waiting on the supervisor. */
  pendingDepositUuids: string[];
  submitting: boolean;
  /** Resolves true when the request was accepted, so the caller can close its dialog. */
  submitRequest: (saleDepositUuid: string, reason: string) => Promise<boolean>;
}

/**
 * The คำขอแก้ไขยอดโอน side of one branch-day, for the route-level report table.
 *
 * Read once for the whole branch-day rather than per row: only one request may be
 * outstanding per deposit, so what every row needs is the same short list of deposit uuids,
 * and asking per row would be a request per สายส่ง on every page.
 *
 * `enabled` is the raising cohort — head office answers these requests rather than raising
 * them, so nothing is fetched on its behalf.
 */
export function useDepositEditRequests(
  branchDay: ClosingBranchDay | null,
  enabled: boolean,
): DepositEditRequests {
  const [pendingDepositUuids, setPendingDepositUuids] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Read as two values rather than as the object: on the standalone report the branch-day is
  // re-derived from the loaded rows, so the object's identity changes on every list refresh
  // while the branch-day it names has not moved.
  const branchCode = branchDay?.branchCode ?? null;
  const businessDate = branchDay?.businessDate ?? null;

  const fetchPending = useCallback(async () => {
    if (!enabled || !branchCode || !businessDate) {
      setPendingDepositUuids([]);
      return;
    }
    try {
      const {
        data: { data },
      } = await getSaleDepositEditRequests(1, PENDING_REQUEST_PAGE_LIMIT, {
        status: "pending",
        dateFrom: businessDate,
        dateTo: businessDate,
        branchCode,
      });
      setPendingDepositUuids(
        (data?.rows ?? []).map((request) => request.sale_deposit_uuid),
      );
    } catch {
      // Already reported by the interceptor. An empty list only means a row is offered a
      // button the server may refuse with a 409 — a far better failure than freezing rows
      // that are genuinely still disputable.
      setPendingDepositUuids([]);
    }
  }, [enabled, branchCode, businessDate]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const submitRequest = useCallback(
    async (saleDepositUuid: string, reason: string): Promise<boolean> => {
      setSubmitting(true);
      try {
        await createSaleDepositEditRequest({
          sale_deposit_uuid: saleDepositUuid,
          reason,
        });
        await fetchPending();
        return true;
      } catch {
        // Refused (already requested, or the window shut while the dialog was open) and
        // reported by the interceptor. Reload so the row explains itself with the state
        // that actually holds instead of re-offering the button.
        await fetchPending();
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [fetchPending],
  );

  return { pendingDepositUuids, submitting, submitRequest };
}
